import * as amplitude from '@amplitude/analytics-browser';
import { plugin as engagementPlugin } from '@amplitude/engagement-browser';
import { useAuthStore } from 'bz-stores/lib/auth-store';
import createContext from 'bz-utilities/hooks/context-v2';
import { history } from 'bz-utilities/modules/networking/history/rr4';
import { type PropsWithChildren, useEffect } from 'react';

const AMPLITUDE_API_KEY = 'ddfb04ddf41883668f3ecbddd89cc38f';

export const isNonProd = !window.location.host.match(
  /(.*).(tm3app.com|click-hub.com|e-clinic.co.uk|privatepracticemanagement.co.uk|caseflow.(ie|co.uk))/gm
);

export const isAlsoNonProd = !!window.location.host.match(
  /(.*)(.(uat|demo|pre-prod|preprod|qa|beta).)(.*)/gm
);

const inactive = isNonProd || isAlsoNonProd;

export type TAmplitude = typeof amplitude;

export const [AnalyticsContext, useAnalytics] = createContext<TAmplitude['track'] | undefined>(
  'AnalyticsContext'
);

export const AnalyticsProvider = ({ children }: PropsWithChildren) => {
  const auth = useAuthStore();
  useEffect(() => {
    if (inactive) return;

    // When the user is not fully authenticated, ensure analytics are reset and opted out.
    if (!auth.authenticated || !auth.user?.id) {
      window.engagement?.shutdown();
      amplitude.reset();
      amplitude.setOptOut(true);
      return;
    }

    // User is fully authenticated; enable and initialise analytics for this user.
    const userId = `${auth.user?.clientId}-${auth.user?.id}-${auth.user?.roleName}`;
    amplitude.setOptOut(false);
    amplitude.init('ddfb04ddf41883668f3ecbddd89cc38f', userId, {
      autocapture: {
        elementInteractions: false,
        formInteractions: false,
        pageViews: true,
        sessions: true,
        frustrationInteractions: true
      },
      identityStorage: 'none'
    });
    amplitude.add(engagementPlugin());
    window.engagement?.setRouter((url) => history.browserHistory.push(url));

    if (auth.user?.clientId) amplitude.setGroup('client', auth.user?.clientId?.toString());
    const identify = new amplitude.Identify();
    identify.set('app', 'click');
    identify.set('analyticsUserId', userId);
    if (auth.user?.clientId) identify.set('clientId', auth.user.clientId);
    if (auth.user?.roleName) identify.set('roleName', auth.user.roleName);
    amplitude.identify(identify);
  }, [auth]);

  return (
    <AnalyticsContext.Provider value={inactive ? undefined : amplitude.track}>
      {children}
    </AnalyticsContext.Provider>
  );
};
